<template>
    <div class="flex flex-col gap-5">
        <Error v-if="error" :text="error" />

        <InputField
            title="Email"
            type="email"
            name="email"
            placeholder="you@example.com"
            hint="Ранее не зарегистрированный в Spotify"
            v-model="email"
        />

        <InputField
            title="Отображаемое имя"
            type="text"
            name="display-name"
            placeholder="MusicLover1997"
            hint="Отображается в твоем профиле"
            v-model="displayName"
        />
        <InputField
            title="Пароль"
            type="password"
            name="password"
            hint="Минимум 8 символов"
            v-model="password"
            :toggleable-password="true"
            :password-visible="arePasswordsVisible"
            :show-password-toggle="false"
        />

        <InputField
            title="Повтор пароля"
            type="password"
            name="password-repeat"
            hint="Минимум 8 символов"
            v-model="passwordRepeat"
            :toggleable-password="true"
            :password-visible="arePasswordsVisible"
            :show-password-toggle="false"
        />

        <button class="self-end text-sm font-medium text-emerald-700" type="button" @click="togglePasswordVisibility">
            {{ arePasswordsVisible ? 'Скрыть пароли' : 'Показать пароли' }}
        </button>

        <div>
            <label for="gender" class="text-base font-medium text-tg-text">Пол</label>
            <fieldset class="mt-1" id="gender">
                <legend class="sr-only">Пол</legend>
                <div class="flex items-center space-x-8">
                    <div class="flex items-center" @click="gender = 'MALE'">
                        <input
                            id="male"
                            name="notification-method"
                            type="radio"
                            :checked="gender === 'MALE'"
                            class="h-4 w-4 border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <label for="male" class="ml-3 block text-sm font-medium text-tg-text">Мужской</label>
                    </div>
                    <div class="flex items-center" @click="gender = 'FEMALE'">
                        <input
                            id="female"
                            name="notification-method"
                            type="radio"
                            :checked="gender === 'FEMALE'"
                            class="h-4 w-4 border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <label for="female" class="ml-3 block text-sm font-medium text-tg-text">Женский</label>
                    </div>
                </div>
            </fieldset>
        </div>

        <InputField title="День рождения" type="date" name="birthday" v-model="birthday" />
    </div>
</template>

<script setup lang="ts">
import { WebApp } from '@grammyjs/web-app'
import { onMounted } from '@vue/runtime-core'
import { Ref, ref } from 'vue'
import api from '../composables/api'
import InputField from '../components/InputField.vue'
import Error from '../components/Error.vue'
import { useError } from '../composables/error'

const error = ref('')
const email = ref('')
const password = ref('')
const passwordRepeat = ref('')
const displayName = ref('')
const birthday = ref('')
const arePasswordsVisible = ref(false)
const gender: Ref<'MALE' | 'FEMALE'> = ref('MALE')

const togglePasswordVisibility = () => {
    arePasswordsVisible.value = !arePasswordsVisible.value
}

const mainButtonClick = async () => {
    if (!email.value || !password.value || !displayName.value || !birthday.value || !gender.value) {
        return useError(`Заполните все поля`)
    }

    if (email.value.trim().includes(' ')) {
        return useError(`Email не должен содержать пробелов`)
    }

    const emailRegexp = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,15})+$/
    if (!emailRegexp.test(email.value)) {
        return useError(`Введен неверный Email`)
    }

    if (password.value.length < 10) {
        return useError('Минимальная длина пароля 10 символов')
    }

    if (!password.value.split('').find((char: string) => !isNaN(+char))) {
        return useError('Пароль должен содержать хотя бы одну цифру')
    }

    if (password.value !== passwordRepeat.value) {
        return useError('Пароль не совпадает')
    }

    const birthdayYear = new Date(birthday.value).getUTCFullYear()
    if (birthdayYear >= 2006) {
        return useError('Пользоваться Spotify можно минимум с 16 лет')
    }

    if (email.value.toLowerCase().endsWith('.ru')) {
        return useError('Почты в зоне .ru больше не поддерживаются')
    }

    WebApp.MainButton.showProgress()

    WebApp.showConfirm('Вы уверены что все данные введены верно?', async (isOk) => {
        if (!isOk) {
            return true
        }

        try {
            await api({
                path: '/accounts/webapp/register',
                method: 'PUT',
                body: {
                    login: email.value.trim(),
                    password: password.value,
                    displayname: displayName.value.trim(),
                    birthday: birthday.value,
                    gender: gender.value
                },
                initData: WebApp.initData
            })

            WebApp.disableClosingConfirmation()
            WebApp.close()
        } catch (err: any) {
            switch (err.message) {
                case 'AUTOACTIVATION.ACCOUNT.NO_REMAINING':
                    error.value = 'Требуется оплата новой подписки, чтобы иметь возможность создать аккаунт'
                    break
                case 'AUTOACTIVATION.ACCOUNT.EXISTS':
                    error.value = 'Такой аккаунт уже добавлен в систему'
                    break
                default:
                    error.value = err.message
            }

            useError('Ошибка, обратитесь в поддержку :(')
        }

        WebApp.MainButton.hideProgress()
    })
}

onMounted(() => {
    WebApp.MainButton.setText('Сохранить')
    WebApp.MainButton.show()
    WebApp.MainButton.onClick(mainButtonClick)
})
</script>
